import { useBatchTransactions } from '@api/hooks/useBatch.ts';
import { PageContainer } from '@components/PageContainer';
import { PermissionGate } from '@components/PermissionGate';
import { Switcher } from '@components/Switcher';
import { PaymentTfa } from '@components/tfa/PaymentTfa.tsx';
import { toastError } from '@components/Toast/Toast.tsx';
import VoPConfirmationBanner from '@components/VoPConfirmationBanner';
import { Collapsible, DEFAULT_PAGE_SIZE, Loader } from '@gurupay/gp-ui-library';
import { BatchMobileItem } from '@pages/batch-operations/batch/components/BatchMobileItem.tsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Navigate, useParams } from 'react-router-dom';
import { VOP_STATUS } from 'types/layout.ts';
import { BatchStatus } from 'types/operations.ts';
import { Capability } from 'types/roles.ts';

import { CollapsibleHeader } from './batch/components/CollapsibleHeader.tsx';
import { ErrorTable } from './batch/components/ErrorTable.tsx';
import { TransactionTable } from './batch/components/TransactionTable.tsx';

export const BatchDetailPage = () => {
	const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
	const { t } = useTranslation('', { keyPrefix: 'payments.batch' });

	const switchOptions = [
		{ label: t('switcher.transactions'), value: 'success' },
		{ label: t('switcher.errors'), value: 'failed' },
	];
	const { batchId } = useParams();
	const [isSigningInitiated, setIsSigningInitiated] = useState(false);
	const [switcherValue, setSwitcherValue] = useState(switchOptions[0].value);

	const { data, isLoading, isError } = useBatchTransactions({
		batchId: batchId!,
		limit: DEFAULT_PAGE_SIZE,
		currentPage: 1,
		filters: {},
	});

	const confirmationKey = `confirmation_closed_${batchId}`;

	const [isVopConfirmed, setIsVopConfirmed] = useState(
		() => !!batchId && sessionStorage.getItem(confirmationKey) === 'true',
	);

	const hasUnmatchedVop = data?.transactions.items.some(
		(item) =>
			['DOMESTIC', 'INTERNAL'].includes(item.payment_type) &&
			item.vopStatus !== VOP_STATUS.MATCH,
	);

	const signinableStatuses = [BatchStatus.UNSIGNED, BatchStatus.UNSIGNED_WITH_WARNINGS];
	const shouldShowVopBanner =
		!!data &&
		!!hasUnmatchedVop &&
		signinableStatuses.includes(data?.status) &&
		!isVopConfirmed;

	const shouldShowTfa =
		!!data?.transactions.items.length &&
		signinableStatuses.includes(data?.status) &&
		!isLoading &&
		(!hasUnmatchedVop || isVopConfirmed);

	const handleConfirm = () => {
		setIsVopConfirmed(true);
		setIsVopConfirmed(sessionStorage.getItem(confirmationKey) === 'true');
	};

	if (isError) {
		toastError(t('error'));
		return <Navigate replace to="/operations/batch" />;
	}

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-[500px]">
				<Loader />
			</div>
		);
	}

	return (
		<PageContainer backUrl="/operations/batch" title={t('title')}>
			{!!data &&
				(isMobile ? (
					<BatchMobileItem item={data} />
				) : (
					<Collapsible
						className="border p-space-16 rounded-md"
						iconPosition="right"
						title={
							<CollapsibleHeader
								isDeleteDisabled={isSigningInitiated}
								isLoading={isLoading}
								item={data}
							/>
						}
						titleClassName="w-full"
						titleKey="file-upload"
					>
						{!!data?.failedToReadEntries?.items.length && (
							<Switcher
								className="mb-space-16"
								onChange={setSwitcherValue}
								options={switchOptions}
								value={switcherValue}
							/>
						)}

						{switcherValue === 'success' ? (
							<TransactionTable
								batchId={batchId!}
								batchStatus={data!.status}
								isDeleteDisabled={isSigningInitiated}
							/>
						) : (
							<ErrorTable
								batchId={batchId!}
								batchStatus={data!.status}
								isDeleteDisabled={isSigningInitiated}
							/>
						)}
					</Collapsible>
				))}

			{shouldShowVopBanner && (
				<VoPConfirmationBanner
					buttonText={t('vop.buttonText')}
					description={t('vop.description')}
					id={batchId!}
					onConfirm={handleConfirm}
					title={t('vop.title')}
				/>
			)}

			{shouldShowTfa && (
				<PermissionGate capability={Capability.SIGN_PAYMENT}>
					<div className="my-space-16">
						<PaymentTfa
							setSigningInitiated={setIsSigningInitiated}
							transactionId={data!.uuid}
							type="batch"
						/>
					</div>
				</PermissionGate>
			)}
		</PageContainer>
	);
};
